import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import Close from '@mui/icons-material/Close';
import { makeStyles } from '@mui/styles';
import Stack from '@mui/material/Stack';
import { useIsMobile } from '../../hooks';
import { formatText } from '../../helpers/text-parser';
import FingoAlert from './alerts/FingoAlert';

const useStyles = makeStyles(() => ({
  icon: {
    fontSize: '5rem',
    margin: 'auto',
    marginBottom: '0.5rem',
    width: '100%',
  },
}));

const FingoDialog = (props) => {
  const {
    open,
    handleClose,
    title,
    subtitle,
    topIcon: TopIcon,
    icon: Icon,
    dialogAlert,
    setDialogAlert,
    children,
    dialogActionButton,
    maxWidth,
    hideCloseButton,
    paddingContent,
    backgroundColor,
    titleBackgroundColor,
    titleColor,
    ...dialogProps
  } = props;
  const isMobile = useIsMobile();
  const classes = useStyles();
  const TopIconComponent = TopIcon?.icon;
  const padding = useMemo(() => {
    const defaultPadding = isMobile ? 4 : 6;
    if (paddingContent === null) {
      return { horizontal: defaultPadding, vertical: defaultPadding };
    }
    if (typeof paddingContent === 'number') {
      return { horizontal: paddingContent, vertical: paddingContent };
    }
    return {
      horizontal: paddingContent.horizontal ?? defaultPadding,
      vertical: paddingContent.vertical ?? defaultPadding,
    };
  }, [isMobile, paddingContent]);
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          borderRadius: 4,
          maxWidth: maxWidth || 770,
        },
      }}
      {...dialogProps}
    >
      <DialogTitle
        component="div"
        sx={{
          borderRadius: '4px 4px 0 0',
          bgcolor: titleBackgroundColor,
          py: { xs: 2, md: 3 },
          pb: { xs: 1, md: 3 },
        }}
      >
        {TopIconComponent && (
        <Stack
          width="100%"
          display="flex"
          justifyContent="center"
          alignItems="center"
          mb={2}
        >
          <TopIconComponent {...TopIcon?.props} />
        </Stack>
        )}
        {typeof title === 'string' ? (
          <Typography color={titleColor} align="center" variant="h4">
            {title}
          </Typography>
        ) : (
          title
        )}
        {!hideCloseButton && (
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 4,
              top: 4,
              color: titleColor,
            }}
          >
            <Close />
          </IconButton>
        )}
      </DialogTitle>
      <FingoAlert alert={dialogAlert} setAlert={setDialogAlert} />
      <DialogContent sx={{ px: padding.horizontal, py: padding.vertical, backgroundColor }}>
        {Icon && <Icon className={classes.icon} />}
        {(subtitle && typeof subtitle === 'string') ? ((
          <Typography variant="medium_2" sx={{ mb: 2, px: 2, color: 'gray.A700', textAlign: 'center' }}>
            {formatText(subtitle)}
          </Typography>
        )) : (
          subtitle
        )}
        {children}
      </DialogContent>
      {dialogActionButton && <DialogActions disableSpacing>{dialogActionButton}</DialogActions>}
    </Dialog>
  );
};

FingoDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  dialogAlert: PropTypes.shape({
    message: PropTypes.string,
    severity: PropTypes.string,
    show: PropTypes.bool,
  }),
  setDialogAlert: PropTypes.func,
  icon: PropTypes.elementType,
  topIcon: PropTypes.shape({
    icon: PropTypes.elementType,
    props: PropTypes.shape({}),
  }),
  children: PropTypes.node,
  dialogActionButton: PropTypes.element,
  maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  hideCloseButton: PropTypes.bool,
  paddingContent: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.shape({
      horizontal: PropTypes.number,
      vertical: PropTypes.number,
    }),
  ]),
  backgroundColor: PropTypes.string,
  titleBackgroundColor: PropTypes.string,
  titleColor: PropTypes.string,
};
FingoDialog.defaultProps = {
  dialogActionButton: null,
  maxWidth: '',
  title: '',
  subtitle: '',
  icon: undefined,
  topIcon: {
    icon: undefined,
    props: {},
  },
  hideCloseButton: false,
  children: null,
  dialogAlert: { message: '', severity: '', show: false },
  setDialogAlert: () => {},
  paddingContent: null,
  backgroundColor: null,
  titleBackgroundColor: 'primary.main',
  titleColor: 'common.white',
};

export default FingoDialog;
